import { useCallback, useEffect, useState } from 'react';
import {
  expintelApi,
  type ExperimentDashboard,
  type ExperimentRecord,
  type ExperimentRecommendation,
  type ExperimentResult,
  type LearningRecord,
  type StatisticalAnalysis,
  type VariantRecord,
} from '../../api/expintel';

type Tab =
  | 'overview'
  | 'variants'
  | 'timeline'
  | 'statistics'
  | 'winners'
  | 'learning'
  | 'recommendations';

function statusClass(s: string) {
  if (s === 'running') return 'text-emerald-700 bg-emerald-50';
  if (s === 'completed' || s === 'winner_selected') return 'text-sky-700 bg-sky-50';
  if (s === 'approval' || s === 'review') return 'text-amber-700 bg-amber-50';
  if (s === 'cancelled') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function riskClass(r?: string) {
  if (r === 'high') return 'text-red-700 bg-red-50';
  if (r === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-emerald-700 bg-emerald-50';
}

export function ExperimentIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ExperimentDashboard | null>(null);
  const [experiments, setExperiments] = useState<ExperimentRecord[]>([]);
  const [variants, setVariants] = useState<VariantRecord[]>([]);
  const [results, setResults] = useState<ExperimentResult[]>([]);
  const [statistics, setStatistics] = useState<StatisticalAnalysis[]>([]);
  const [learning, setLearning] = useState<LearningRecord[]>([]);
  const [recommendations, setRecommendations] = useState<ExperimentRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, exps, vars, res, stats, learn, recs] = await Promise.all([
        expintelApi.dashboard(),
        expintelApi.listExperiments(),
        expintelApi.listVariants(),
        expintelApi.listResults(),
        expintelApi.listStatistics(),
        expintelApi.listLearning(),
        expintelApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setExperiments(exps);
      setVariants(vars);
      setResults(res);
      setStatistics(stats);
      setLearning(learn);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load experimentation data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await expintelApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Experiment Dashboard' },
    { id: 'variants', label: 'Variant Designer' },
    { id: 'timeline', label: 'Experiment Timeline' },
    { id: 'statistics', label: 'Statistical Analysis' },
    { id: 'winners', label: 'Winner Comparison' },
    { id: 'learning', label: 'Learning Explorer' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Amazon Experimentation Intelligence</h1>
          <p className="text-sm text-slate-500">
            Manage Your Experiments (MYE), AI experimentation &amp; continuous optimization — FRP-00002
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Experiments', value: dashboard.active_experiments.toString() },
              { label: 'Completed', value: dashboard.completed_experiments.toString() },
              { label: 'Pending Approval', value: dashboard.pending_approval.toString() },
              { label: 'Winning Variants', value: dashboard.winning_variants.toString() },
              { label: 'Avg Lift', value: `${dashboard.avg_lift_pct.toFixed(1)}%` },
              { label: 'Avg Confidence', value: `${(dashboard.avg_confidence * 100).toFixed(0)}%` },
              { label: 'Learning Updates', value: dashboard.learning_updates.toString() },
              { label: 'MYE Connected', value: dashboard.mye_connected ? 'Yes' : 'No' },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="font-medium text-slate-900">Active &amp; Recent Experiments</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Expected Lift</th>
                    <th className="px-4 py-2">MYE</th>
                    <th className="px-4 py-2">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {experiments.slice(0, 8).map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-medium text-slate-900">{e.name}</td>
                      <td className="px-4 py-2 text-slate-600">{e.type.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(e.status)}`}>
                          {e.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2">{e.expected_lift_pct.toFixed(1)}%</td>
                      <td className="px-4 py-2">{e.mye_eligible ? 'Eligible' : 'Internal'}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskClass(e.risk_level)}`}>
                          {e.risk_level ?? 'low'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'variants' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-medium text-slate-900">Variant Designer</h2>
            <p className="text-sm text-slate-500">Baseline and variant versions with complete version history</p>
          </div>
          <div className="divide-y divide-slate-100">
            {variants.map((v) => (
              <div key={v.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{v.label}</span>
                  {v.is_baseline && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">baseline</span>
                  )}
                  <span className="text-xs text-slate-400">exp: {v.experiment_id}</span>
                </div>
                {v.description && <p className="mt-1 text-sm text-slate-600">{v.description}</p>}
                {v.content && (
                  <p className="mt-1 rounded bg-slate-50 px-2 py-1 text-sm text-slate-700">{v.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'timeline' && (
        <div className="space-y-3">
          {experiments.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium text-slate-900">{e.name}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(e.status)}`}>
                  {e.status.replace(/_/g, ' ')}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">{e.hypothesis}</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Created: {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</span>
                {e.started_at && <span>Started: {new Date(e.started_at).toLocaleDateString()}</span>}
                {e.completed_at && <span>Completed: {new Date(e.completed_at).toLocaleDateString()}</span>}
                <span>Duration: {e.expected_duration ?? '—'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'statistics' && (
        <div className="space-y-4">
          {statistics.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-900">Experiment {s.experiment_id}</span>
                {s.is_significant ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    Statistically Significant
                  </span>
                ) : s.is_directional_only ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                    Directional Trend Only
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Inconclusive</span>
                )}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Observed Lift</p>
                  <p className="font-medium">{s.observed_lift_pct.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Significance</p>
                  <p className="font-medium">{(s.statistical_significance * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Confidence Interval</p>
                  <p className="font-medium">{s.confidence_interval ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Sample</p>
                  <p className="font-medium">
                    {s.current_sample.toLocaleString()} / {s.min_sample_size.toLocaleString()}
                  </p>
                </div>
              </div>
              {(s.outliers_detected || s.incomplete) && (
                <p className="mt-2 text-xs text-amber-700">
                  {s.outliers_detected && 'Outliers detected. '}
                  {s.incomplete && 'Experiment incomplete — do not select winner yet.'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'winners' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-medium text-slate-900">Winner Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">Variant</th>
                  <th className="px-4 py-2">CTR</th>
                  <th className="px-4 py-2">CVR</th>
                  <th className="px-4 py-2">Orders</th>
                  <th className="px-4 py-2">Revenue</th>
                  <th className="px-4 py-2">Profit</th>
                  <th className="px-4 py-2">Winner</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium">{r.variant_label}</td>
                    <td className="px-4 py-2">{(r.ctr * 100).toFixed(2)}%</td>
                    <td className="px-4 py-2">{(r.conversion_rate * 100).toFixed(2)}%</td>
                    <td className="px-4 py-2">{r.orders.toLocaleString()}</td>
                    <td className="px-4 py-2">${r.revenue.toLocaleString()}</td>
                    <td className="px-4 py-2">${r.profit.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      {r.is_winner ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Winner
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'learning' && (
        <div className="space-y-3">
          {learning.map((l) => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
                  {l.dna_target.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-400">v{l.version}</span>
              </div>
              <p className="mt-2 text-sm text-slate-700">{l.learning}</p>
              {l.impact && <p className="mt-1 text-xs text-slate-500">Impact: {l.impact}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  Priority #{r.priority_rank}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Lift: {r.expected_lift_pct.toFixed(1)}%</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                <span>ROI: {r.expected_roi.toFixed(1)}x</span>
                <span>Sample: {r.required_sample_size.toLocaleString()}</span>
                <span className={riskClass(r.risk_level)}>{r.risk_level ?? 'low'} risk</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
